<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api, asList } from '../api/client'
import {
  inquiryStatusLabels,
  normalizeInquiry,
  normalizeResource,
  options,
  serializeBooking,
} from '../api/mappers'
import { formatDateTime } from '../utils'
import AppIcon from '../components/AppIcon.vue'
import BookingDialog from '../components/BookingDialog.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import ModalDialog from '../components/ModalDialog.vue'
import { useToast } from '../composables/useToast'

const route = useRoute()
const { show } = useToast()

const inquiries = ref([])
const resources = ref([])
const loading = ref(true)
const changingStatus = ref(false)
const converting = ref(false)
const search = ref('')
const statusFilter = ref('')
const selectedInquiry = ref(null)
const detailOpen = ref(false)
const bookingInquiry = ref(null)
const bookingDialogOpen = ref(false)

const visibleInquiries = computed(() => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  return inquiries.value.filter((inquiry) => {
    if (statusFilter.value && inquiry.status !== statusFilter.value) return false
    if (!term) return true
    return [
      inquiry.id,
      inquiry.externalId,
      inquiry.guestName,
      inquiry.guestPhone,
      inquiry.guestEmail,
      inquiry.resource.name,
      inquiry.comment,
    ].some((value) => String(value || '').toLocaleLowerCase('ru').includes(term))
  })
})

const newCount = computed(() => inquiries.value.filter((inquiry) => inquiry.status === 'new').length)
const selectedResource = computed(() => resources.value.find((resource) => (
  String(resource.id) === String(selectedInquiry.value?.resourceId)
)))
const canConvert = computed(() => (
  selectedInquiry.value
  && ['new', 'contacted'].includes(selectedInquiry.value.status)
  && selectedResource.value?.isActive
))

async function loadResources() {
  try {
    const data = await api.resources.list()
    resources.value = asList(data, ['resources']).map(normalizeResource)
  } catch (error) {
    show(error.message || 'Не удалось загрузить объекты для создания брони.', 'error')
  }
}

async function loadInquiries() {
  loading.value = true
  try {
    const data = await api.inquiries.listAll()
    inquiries.value = data
      .map(normalizeInquiry)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id)
  } catch (error) {
    show(error.message || 'Не удалось загрузить заявки.', 'error')
  } finally {
    loading.value = false
  }
}

function replaceInquiry(raw) {
  const inquiry = normalizeInquiry(raw)
  inquiries.value = inquiries.value.map((item) => item.id === inquiry.id ? inquiry : item)
  if (selectedInquiry.value?.id === inquiry.id) selectedInquiry.value = inquiry
}

async function openDetail(inquiry) {
  selectedInquiry.value = inquiry
  detailOpen.value = true
  try {
    replaceInquiry(await api.inquiries.get(inquiry.id))
  } catch (error) {
    show(error.message || 'Не удалось обновить заявку.', 'error')
  }
}

function closeDetail() {
  if (!changingStatus.value) detailOpen.value = false
}

async function updateStatus(status) {
  if (!selectedInquiry.value || changingStatus.value) return
  changingStatus.value = true
  try {
    replaceInquiry(await api.inquiries.updateStatus(selectedInquiry.value.id, status))
    show(status === 'contacted' ? 'Заявка взята в работу.' : status === 'closed' ? 'Заявка закрыта.' : 'Заявка снова в списке новых.')
  } catch (error) {
    show(error.message || 'Не удалось изменить статус заявки.', 'error')
  } finally {
    changingStatus.value = false
  }
}

async function recordContact() {
  if (!selectedInquiry.value || changingStatus.value) return
  changingStatus.value = true
  try {
    replaceInquiry(await api.inquiries.recordContact(selectedInquiry.value.id))
    show('Общение с гостем отмечено.')
  } catch (error) {
    show(error.message || 'Не удалось отметить общение с гостем.', 'error')
  } finally {
    changingStatus.value = false
  }
}

async function openConvert() {
  if (!canConvert.value) return
  bookingInquiry.value = selectedInquiry.value
  detailOpen.value = false
  await nextTick()
  bookingDialogOpen.value = true
}

function closeBookingDialog() {
  if (!converting.value) bookingDialogOpen.value = false
}

async function convertInquiry(form) {
  if (!bookingInquiry.value || converting.value) return
  converting.value = true
  try {
    const result = await api.inquiries.convert(bookingInquiry.value.id, serializeBooking(form))
    replaceInquiry(result.inquiry)
    const bookingId = result.booking?.id
    show(bookingId ? `Бронь №${bookingId} создана по заявке.` : 'Бронь создана по заявке.')
    bookingDialogOpen.value = false
    bookingInquiry.value = null
  } catch (error) {
    show(error.message || 'Не удалось создать бронь. Проверьте занятость объекта и данные заявки.', 'error')
  } finally {
    converting.value = false
  }
}

function period(inquiry) {
  if (!inquiry.startsAt && !inquiry.endsAt) return 'Не указан'
  if (inquiry.startsAt && inquiry.endsAt) {
    return `${formatDateTime(inquiry.startsAt)} — ${formatDateTime(inquiry.endsAt)}`
  }
  return inquiry.startsAt
    ? `С ${formatDateTime(inquiry.startsAt)}`
    : `До ${formatDateTime(inquiry.endsAt)}`
}

onMounted(async () => {
  await Promise.all([loadResources(), loadInquiries()])
  const inquiryId = Number(route.query.id)
  if (Number.isSafeInteger(inquiryId) && inquiryId > 0) {
    const existing = inquiries.value.find((inquiry) => inquiry.id === inquiryId)
    if (existing) await openDetail(existing)
  }
})
</script>

<template>
  <div class="page inquiries-page">
    <header class="page-header">
      <div>
        <span class="eyebrow">Обращения гостей</span>
        <h1>Заявки с сайта</h1>
        <p class="page-subtitle">Заявка не блокирует объект. Перед подтверждением проверьте даты и создайте бронь.</p>
      </div>
      <button type="button" class="button button--secondary" :disabled="loading" @click="loadInquiries">
        <AppIcon name="refresh" :size="17" />
        Обновить
      </button>
    </header>

    <section class="filter-bar" aria-label="Фильтры заявок">
      <label class="search-field">
        <AppIcon name="search" :size="18" />
        <span class="sr-only">Поиск заявок</span>
        <input v-model="search" type="search" placeholder="Гость, телефон, объект или №" />
      </label>
      <label class="compact-field">
        <span class="sr-only">Статус заявки</span>
        <select v-model="statusFilter">
          <option value="">Все статусы</option>
          <option v-for="status in options.inquiryStatuses" :key="status.value" :value="status.value">{{ status.label }}</option>
        </select>
      </label>
      <span v-if="newCount" class="inquiries-new-count">Новых: {{ newCount }}</span>
    </section>

    <LoadingState v-if="loading" />

    <section v-else class="data-section" aria-labelledby="inquiries-list-heading">
      <header class="section-header section-header--compact">
        <div>
          <h2 id="inquiries-list-heading">Входящие обращения</h2>
          <p>{{ visibleInquiries.length }} {{ visibleInquiries.length === 1 ? 'запись' : 'записей' }}</p>
        </div>
      </header>

      <div v-if="visibleInquiries.length" class="table-scroll">
        <table class="data-table inquiries-table">
          <caption class="sr-only">Заявки на бронирование с сайта</caption>
          <thead>
            <tr>
              <th scope="col">Гость</th>
              <th scope="col">Объект</th>
              <th scope="col">Пожелания</th>
              <th scope="col">Получена</th>
              <th scope="col">Статус</th>
              <th scope="col"><span class="sr-only">Действия</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inquiry in visibleInquiries" :key="inquiry.id" :class="{ 'row--muted': inquiry.status === 'closed' }">
              <td data-label="Гость">
                <strong>{{ inquiry.guestName }}</strong>
                <span class="cell-secondary">
                  <a v-if="inquiry.guestPhone" :href="`tel:${inquiry.guestPhone}`">{{ inquiry.guestPhone }}</a>
                  <span v-else>№{{ inquiry.id }}</span>
                </span>
              </td>
              <td data-label="Объект"><strong>{{ inquiry.resource.name }}</strong></td>
              <td data-label="Пожелания">
                <strong>{{ period(inquiry) }}</strong>
                <span v-if="inquiry.guestsCount" class="cell-secondary">{{ inquiry.guestsCount }} чел.</span>
              </td>
              <td data-label="Получена">{{ formatDateTime(inquiry.createdAt) }}</td>
              <td data-label="Статус">
                <span class="status-badge" :class="`status-badge--inquiry-${inquiry.status}`">
                  <span class="status-dot" aria-hidden="true" />{{ inquiryStatusLabels[inquiry.status] }}
                </span>
              </td>
              <td class="row-actions" data-label="Действия">
                <button type="button" class="icon-button icon-button--bordered" :aria-label="`Открыть заявку №${inquiry.id}`" title="Открыть заявку" @click="openDetail(inquiry)">
                  <AppIcon name="arrow-right" :size="17" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <EmptyState v-else title="Заявки не найдены" text="Проверьте поисковый запрос или выберите другой статус.">
        <button v-if="search || statusFilter" type="button" class="button button--secondary" @click="search = ''; statusFilter = ''">Сбросить фильтры</button>
      </EmptyState>
    </section>

    <ModalDialog :open="detailOpen" :title="`Заявка №${selectedInquiry?.id || ''}`" eyebrow="Обращение с сайта" @close="closeDetail">
      <template v-if="selectedInquiry">
        <div class="inquiry-detail-head">
          <div>
            <strong>{{ selectedInquiry.guestName }}</strong>
            <span>Получена {{ formatDateTime(selectedInquiry.createdAt) }}</span>
          </div>
          <span class="status-badge" :class="`status-badge--inquiry-${selectedInquiry.status}`">
            <span class="status-dot" aria-hidden="true" />{{ inquiryStatusLabels[selectedInquiry.status] }}
          </span>
        </div>

        <dl class="inquiry-details">
          <div><dt>Объект</dt><dd>{{ selectedInquiry.resource.name }}</dd></div>
          <div><dt>Желаемый период</dt><dd>{{ period(selectedInquiry) }}</dd></div>
          <div><dt>Количество гостей</dt><dd>{{ selectedInquiry.guestsCount ? `${selectedInquiry.guestsCount} чел.` : 'Не указано' }}</dd></div>
          <div><dt>Телефон</dt><dd><a v-if="selectedInquiry.guestPhone" :href="`tel:${selectedInquiry.guestPhone}`">{{ selectedInquiry.guestPhone }}</a><span v-else>—</span></dd></div>
          <div><dt>Электронная почта</dt><dd><a v-if="selectedInquiry.guestEmail" :href="`mailto:${selectedInquiry.guestEmail}`">{{ selectedInquiry.guestEmail }}</a><span v-else>—</span></dd></div>
          <div v-if="selectedInquiry.comment" class="inquiry-details__full"><dt>Комментарий гостя</dt><dd class="inquiry-comment">{{ selectedInquiry.comment }}</dd></div>
        </dl>

        <p v-if="!selectedResource?.isActive && selectedInquiry.status !== 'converted'" class="inquiry-warning" role="status">Объект отключён или недоступен в CRM. Создать бронь по этой заявке нельзя.</p>
        <p v-else-if="canConvert && (!selectedInquiry.startsAt || !selectedInquiry.endsAt)" class="inquiry-warning" role="status">Даты не указаны гостем. Уточните их перед созданием брони.</p>
        <p v-if="selectedInquiry.status === 'closed'" class="inquiry-warning" role="status">Рассмотрение завершено. Для нового обращения гость отправляет новую заявку.</p>

        <footer class="modal-actions inquiry-actions">
          <RouterLink v-if="selectedInquiry.bookingId" class="button button--secondary" :to="{ name: 'bookings', query: { booking: selectedInquiry.bookingId } }" @click="detailOpen = false">Открыть бронь №{{ selectedInquiry.bookingId }}</RouterLink>
          <button v-if="selectedInquiry.status === 'new'" type="button" class="button button--secondary" :disabled="changingStatus" @click="updateStatus('contacted')">Взять в работу</button>
          <button v-if="['new', 'contacted'].includes(selectedInquiry.status)" type="button" class="button button--secondary" :disabled="changingStatus" @click="recordContact">Общение с гостем состоялось</button>
          <button v-if="['new', 'contacted'].includes(selectedInquiry.status)" type="button" class="button button--ghost" :disabled="changingStatus" @click="updateStatus('closed')">Закрыть заявку</button>
          <button v-if="['new', 'contacted'].includes(selectedInquiry.status)" type="button" class="button button--primary" :disabled="!canConvert || changingStatus" @click="openConvert">Создать бронь</button>
        </footer>
      </template>
    </ModalDialog>

    <BookingDialog
      :open="bookingDialogOpen"
      :initial-inquiry="bookingInquiry"
      :resources="resources"
      :saving="converting"
      @close="closeBookingDialog"
      @save="convertInquiry"
    />
  </div>
</template>
